'use strict';

/**
 * Utilita pre prácu s cestami v JSON objektoch pomocou dot notácie.
 *
 * Podporuje vnorené objekty, polia a číselné indexy.
 * Príklady ciest: `"db.password"`, `"repos.map_orto.fallbacks.0"`.
 *
 * @class
 */
class KeyPath {
  /**
   * Rozdelí dot notáciu na pole segmentov.
   *
   * @static
   * @param {string} path - Cesta v dot notácii (napr. "db.connect.password")
   * @returns {string[]} Pole segmentov cesty
   * @throws {Error} Ak je cesta prázdna alebo neplatná
   */
  static parse(path) {
    if (!path || typeof path !== 'string') {
      throw new Error(`Neplatná cesta: "${path}". Cesta musí byť neprázdny reťazec.`);
    }
    const segments = path.split('.');
    if (segments.some((s) => s === '')) {
      throw new Error(`Neplatná cesta: "${path}". Cesta nesmie obsahovať prázdne segmenty.`);
    }
    return segments;
  }

  /**
   * Získa hodnotu z objektu na zadanej ceste.
   *
   * @static
   * @param {Object} obj - Zdrojový objekt
   * @param {string} path - Cesta v dot notácii
   * @returns {*} Hodnota na ceste alebo undefined ak neexistuje
   */
  static get(obj, path) {
    const segments = KeyPath.parse(path);
    let current = obj;
    for (const segment of segments) {
      if (current === null || current === undefined || typeof current !== 'object') {
        return undefined;
      }
      current = current[segment];
    }
    return current;
  }

  /**
   * Nastaví hodnotu v objekte na zadanej ceste.
   * Vytvára vnorené objekty podľa potreby.
   *
   * @static
   * @param {Object} obj - Cieľový objekt (mutuje sa)
   * @param {string} path - Cesta v dot notácii
   * @param {*} value - Hodnota na nastavenie
   * @throws {Error} Ak rodičovský segment nie je objekt ani pole
   */
  static set(obj, path, value) {
    const segments = KeyPath.parse(path);
    let current = obj;
    for (let i = 0; i < segments.length - 1; i++) {
      const segment = segments[i];
      if (current[segment] === undefined || current[segment] === null) {
        current[segment] = {};
      }
      if (typeof current[segment] !== 'object') {
        throw new Error(
          `Nemôžem nastaviť cestu "${path}": ` +
          `segment "${segment}" nie je objekt ani pole.`,
        );
      }
      current = current[segment];
    }
    current[segments[segments.length - 1]] = value;
  }

  /**
   * Rozparsuje zoznam kľúčov z CLI argumentov.
   *
   * Podporuje:
   * - Viacero --key argumentov: `["db.password", "api.secret"]`
   * - Čiarkou oddelené: `["db.password,api.secret"]`
   * - Kombináciu oboch
   *
   * @static
   * @param {string[]} keys - Pole reťazcov z CLI
   * @returns {string[]} Unikátne cesty v dot notácii
   */
  static parseKeyArgs(keys) {
    /** @type {Set<string>} */
    const result = new Set();
    for (const key of keys) {
      const parts = key.split(',');
      for (const part of parts) {
        const trimmed = part.trim();
        if (trimmed) {
          result.add(trimmed);
        }
      }
    }
    return [...result];
  }
}

module.exports = KeyPath;
